import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  CreditCard,
  Loader2,
  Phone,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import SettingsShell from './SettingsShell';
import { billingAPI } from '../../services/api';

const BillingSettings = () => {
  const { user, refreshSubscription } = useAuth();
  const isOwner = user?.role === 'owner';

  const [productCode, setProductCode] = useState('');
  const [purchaseKind, setPurchaseKind] = useState('');
  const [sub, setSub] = useState(user?.subscription || null);
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState('');
  const [payPhone, setPayPhone] = useState('');
  const [paying, setPaying] = useState(false);
  const [payRef, setPayRef] = useState(null);
  const [payMessage, setPayMessage] = useState('');
  const [paySuccess, setPaySuccess] = useState(false);

  const loadSubscription = useCallback(async (showLoading = true) => {
    if (showLoading) setSubLoading(true);
    setSubError('');
    try {
      const data = await refreshSubscription();
      setSub(data);
      setProductCode((prev) => prev || (data.plans?.some(plan => plan.code === data.planCode) ? data.planCode : 'core'));
      if (data.pendingPayment) { setPayRef(data.pendingPayment.reference); setPurchaseKind(data.pendingPayment.product_code); setPaySuccess(false); }
      if (data?.payerPhone) setPayPhone((prev) => prev || data.payerPhone);
    } catch (err) {
      setSub(null);
      const msg = err?.response?.data?.error || err?.message || 'Failed to load subscription';
      setSubError(typeof msg === 'string' ? msg : 'Failed to load subscription');
    } finally {
      setSubLoading(false);
    }
  }, [refreshSubscription]);

  useEffect(() => {
    if (isOwner) loadSubscription();
  }, [isOwner, loadSubscription]);

  useEffect(() => {
    setSub(user?.subscription || null);
  }, [user?.subscription]);

  useEffect(() => {
    if (!payRef || paySuccess) return;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await billingAPI.getTransaction(payRef);
        if (!active) return;
        const paidStatuses = ['paid', 'awaiting delivery', 'delivered'];
        if (data.applied && paidStatuses.includes((data.status || '').toLowerCase())) {
          setPaySuccess(true);
          setPayMessage(data.productCode === 'assisted_setup' ? 'Assisted setup paid. Contact support@leadmarka.co.zw to arrange your setup.' : 'Payment successful! Your selected plan is now active.');
          setPayRef(null);
          loadSubscription(false);
        } else if (data.needsAttention) {
          setPayMessage('Payment status could not be confirmed. Contact support@leadmarka.co.zw with reference ' + payRef + ' before paying again.');
        } else if (['cancelled', 'refunded', 'error'].includes((data.status || '').toLowerCase())) {
          setPayMessage(`Payment ${data.status.toLowerCase()}. Please try again.`);
          setPayRef(null);
        }
      } catch (err) {
        // ignore poll errors
      }
    };
    const interval = setInterval(poll, 4000);
    poll();
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [payRef, paySuccess, loadSubscription]);

  const handlePay = async (code = productCode) => {
    if (!payPhone.trim()) return;
    setPaying(true);
    setPayMessage('');
    setPaySuccess(false);
    setSubError('');
    try {
      const { data } = await billingAPI.startEcocash({ phone: payPhone.trim(), productCode: code });
      setPayRef(data.reference);
      setPurchaseKind(code);
      setPayMessage(data.instructions || 'Check your phone to confirm the payment.');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Payment failed';
      setSubError(typeof msg === 'string' ? msg : 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const selectedPlan = sub?.plans?.find(plan => plan.code === productCode);

  if (!isOwner) {
    return (
      <SettingsShell
        title="Billing"
        description="Billing is managed by the workspace owner."
      >
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
          Only workspace owners can update billing or renew subscriptions.
        </div>
      </SettingsShell>
    );
  }

  return (
    <SettingsShell
      title="Billing"
      description="Review your plan, renewal, and payment details."
    >
      <div className={`rounded-lg p-4 border shadow-sm ${sub?.status === 'read_only'
        ? 'bg-red-50 border-red-200'
        : sub?.status === 'grace'
          ? 'bg-yellow-50 border-yellow-200'
          : 'bg-white border-gray-200'
        }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${sub?.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
              <CreditCard className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 leading-tight">
                {sub?.plan || 'Your subscription'}
              </h3>
              <p className="text-xs text-gray-500 font-medium lowercase">
                {sub?.status === 'active' ? 'Current plan' : 'Subscription status'}
              </p>
            </div>
          </div>
          {subLoading && <Loader2 className="w-4 h-4 animate-spin text-primary-500" />}
        </div>

        {sub?.paynowMode === 'test' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-800 text-xs mb-4">
            <div className="font-bold uppercase tracking-tight text-xs text-yellow-700 mb-1">Test Mode Active</div>
            <div className="flex flex-wrap gap-2 items-center">
              EcoCash test numbers:
              <span className="bg-white/50 px-1 py-0.5 rounded font-mono font-bold">0771111111</span>
              <span className="bg-white/50 px-1 py-0.5 rounded font-mono font-bold">0774444444</span>
            </div>
          </div>
        )}

        {subError && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs font-medium mb-4">
            {subError}
          </div>
        )}

        {paySuccess && (
          <div role="status" className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-xs font-semibold flex items-center gap-2 mb-4">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            {payMessage}
          </div>
        )}

        {sub && !subLoading && (
          <div className="text-sm space-y-2 mb-4 bg-gray-50/50 p-3 rounded-lg border border-gray-100">
            {sub.status === 'trialing' && (
              <div className="space-y-0.5">
                <p className="font-bold text-primary-700">
                  Free trial — Day {Math.max(1, 8 - (sub.trialDaysLeft || 0))} of 7
                </p>
                <p className="text-xs text-gray-500 font-medium">
                  Choose a plan to keep your workspace active after the trial.
                </p>
              </div>
            )}
            {sub.status === 'grace' && (
              <p className="font-bold text-yellow-700 flex items-center gap-2 text-xs">
                <AlertTriangle className="w-4 h-4" />
                Trial ended — grace period active
              </p>
            )}
            {sub.status === 'read_only' && (
              <p className="font-bold text-red-700 flex items-center gap-2 text-xs">
                <AlertTriangle className="w-4 h-4" />
                Subscription expired — account read-only
              </p>
            )}
            {sub.status === 'active' && (
              <div className="space-y-0.5">
                <p className="font-bold text-green-700 flex items-center gap-1.5 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Active Membership
                </p>
                {sub.currentPeriodEnd && (
                  <p className="text-xs text-gray-500 font-medium ml-5">
                    Access until: {new Date(sub.currentPeriodEnd).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {sub && !subLoading && sub.plans && (
          <div className="space-y-4">
            <fieldset disabled={paying || !!payRef} className="space-y-3">
              <legend className="font-semibold text-gray-900">Choose your plan</legend>
              {sub.plans.map(plan => (
                <label key={plan.code} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${productCode === plan.code ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
                  <input type="radio" name="billing-plan" value={plan.code} checked={productCode === plan.code} onChange={() => setProductCode(plan.code)} className="mt-1" />
                  <span className="min-w-0">
                    <strong>{plan.name} — US${plan.amount}/month</strong>
                    <span className="mt-1 block text-sm font-medium text-gray-800">{plan.code === 'core' ? 'Your team runs the CRM.' : plan.code === 'ai_starter' ? 'AI helps your team respond.' : 'AI handles routine conversations.'}</span>
                    <span className="mt-1 block text-sm text-gray-600">{plan.code === 'core' ? 'Contacts, enquiries, shared inbox and manual follow-ups. No AI.' : plan.code === 'ai_starter' ? 'Review and send AI drafts. Approve suggested enquiry details before saving. Schedule follow-ups yourself.' : 'Everything in Starter, plus automatic WhatsApp replies, automatic enquiry saves and AI-scheduled follow-ups. Your team can take over at any time.'}</span>
                    {plan.replies > 0 && <span className="mt-2 block text-xs text-gray-600">{plan.replies.toLocaleString()} AI drafts / month · 60 voice minutes · 1,000 capture passes</span>}
                  </span>
                </label>
              ))}
            </fieldset>
            <p className="text-xs text-gray-600">Both AI plans use the same AI model and your business knowledge. Growth automation applies to WhatsApp and must be enabled in settings. AI follow-ups are reminders for your team, not automatic messages to customers.</p>
            {sub.automationGrandfathered && <p role="status" className="rounded-lg bg-primary-50 p-3 text-sm text-primary-800">Your existing Starter subscription retains automation at its current price while you stay on this plan. Switching plans ends this retained benefit.</p>}
            <p className="text-sm text-gray-600">Each payment buys 30 days. Payments are manual; there is no automatic debit. Plan changes take effect after payment, with unused paid time converted at the old and new plan prices.</p>
            {sub.planCode !== 'core' && sub.usage && <div className="rounded-lg bg-gray-50 p-3 text-sm"><strong>AI replies this calendar month: {sub.usage.replies.toLocaleString()} / {sub.usage.limit.toLocaleString()}</strong><p>Completed AI drafts (including samples) count toward usage, even if not sent. Failed or stale drafts do not count. Allowances reset on the first of each month in UTC; renewing or changing plans does not reset usage.</p><p>AI plans also retain 60 voice-transcription minutes and 1,000 enquiry-capture passes per calendar month. At a limit, automation pauses and human replies remain available.</p></div>}
            <div>
              <label htmlFor="billing-ecocash-number" className="block text-sm font-medium text-gray-700 mb-1">
                EcoCash number (USD)
              </label>
              <div className="flex flex-col gap-2">
                <div className="relative group">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                  <input
                    id="billing-ecocash-number"
                    type="tel"
                    value={payPhone}
                    onChange={(event) => setPayPhone(event.target.value)}
                    placeholder="07XX XXX XXX"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-gray-900 font-medium transition-all"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handlePay()}
                  disabled={paying || !payPhone.trim() || !!payRef || !selectedPlan}
                  className="w-full bg-primary-600 text-white p-3 rounded-lg text-sm font-medium hover:bg-primary-700 active:bg-primary-800 transition-all disabled:opacity-50"
                >
                  {paying ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2 inline" />
                      Processing...
                    </>
                  ) : (
                    `Pay US$${selectedPlan?.amount || 0} for ${selectedPlan?.name || 'selected plan'}`
                  )}
                </button>
              </div>
            </div>

            {payRef && !paySuccess && (
              <div role="status" className="bg-primary-50 border border-primary-100 rounded-lg p-3 text-primary-700 text-xs font-semibold flex items-center gap-2 animate-pulse">
                <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                <span>{payMessage || `Checking ${purchaseKind === 'assisted_setup' ? 'assisted setup' : 'subscription'} payment...`}</span>
              </div>
            )}

            {!payRef && payMessage && !paySuccess && (
              <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs font-medium">
                {payMessage}
              </div>
            )}

            <div className="rounded-lg border border-gray-200 p-3 space-y-2">
              <h4 className="font-semibold">Assisted setup — US$100 once</h4>
              <p className="text-sm text-gray-600">Optional, separate from your subscription. This payment does not renew or change your plan.</p>
              {sub.assistedSetup?.paidAt ? <p className="text-sm text-green-700">Paid — contact support@leadmarka.co.zw to arrange setup.</p> : <button type="button" onClick={() => handlePay('assisted_setup')} disabled={paying || !!payRef || !payPhone.trim()} className="rounded-lg border border-primary-600 px-4 py-2 text-primary-700 disabled:opacity-50">Pay US$100 for assisted setup</button>}
            </div>
          </div>
        )}
      </div>
    </SettingsShell>
  );
};

export default BillingSettings;
